import React, { useEffect, useState } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { inventoryLabelsService } from '../services/InventoryLabelsService';
import { useSubdomain } from '../contexts/SubdomainContext';
import { appNavigate } from '../utils/appNavigate';
import { labelLinkPickPath } from '../utils/labelLinkPaths';
import { useMobileLayout } from '../hooks/useMobileLayout';
import type { InventoryLabelResolveResponse } from '@cnapse/shared/inventory-label.js';
import { parseInventoryLabelInput } from '@cnapse/shared/inventory-label.js';

interface InventoryLabelLandingProps {
  barcodeRaw: string;
}

export const InventoryLabelLanding: React.FC<InventoryLabelLandingProps> = ({ barcodeRaw }) => {
  const { subdomain } = useSubdomain();
  const isMobile = useMobileLayout();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const run = async (): Promise<void> => {
      const barcode = parseInventoryLabelInput(barcodeRaw);
      if (!barcode) {
        setError('Invalid label format. Expected CNP- followed by 12 characters.');
        return;
      }

      try {
        const result: InventoryLabelResolveResponse = await inventoryLabelsService.resolveInventoryLabel(barcode);
        if (cancelled) {
          return;
        }

        const prefix = subdomain ? `/${subdomain}` : '';

        if (result.status === 'linked' && result.entityType === 'container') {
          appNavigate(`${prefix}/containers?edit=${result.entityId}&search=${encodeURIComponent(result.barcode)}`);
          return;
        }

        if (result.status === 'linked' && result.entityType === 'cage') {
          appNavigate(`${prefix}/cages?search=${encodeURIComponent(result.barcode)}`);
          return;
        }

        if (result.status === 'unassigned') {
          if (isMobile) {
            appNavigate(labelLinkPickPath(subdomain, result.barcode));
          } else {
            appNavigate(`${prefix}/containers?assignLabel=${encodeURIComponent(result.barcode)}`);
          }
          return;
        }

        setError('This label could not be resolved.');
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to resolve label');
        }
      }
    };

    void run();
    return () => {
      cancelled = true;
    };
  }, [barcodeRaw, subdomain, isMobile]);

  if (error) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 240, p: 4 }}>
      <CircularProgress />
      <Typography sx={{ ml: 2 }}>Looking up label…</Typography>
    </Box>
  );
};

export default InventoryLabelLanding;
